import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

/** Row from GetModules / GetMenu (lookup-style) */
export interface PermissionLookupItem {
  id: number
  lookupCodeId: number
  lookupNameEn: string
  lookupNameAr?: string
  isActive: number
  parentCode: number
  /** When API returns existing role-permission row (edit) */
  lineId?: number
  /** Present on menu rows when API includes permission flags */
  canRead?: boolean
  canUpdate?: boolean
  canDelete?: boolean
  canInsert?: boolean
}

/** Backend save body (property name matches API spelling) */
export interface MyPermissionVm {
  lineId: number
  roleId: number
  moduleId: number
  menuId: number
  canRead: number
  canUpdate: number
  canDelete: number
  canInsert: number
  isDeleted: boolean
}

export interface SavePermissionsPayload {
  actionType: string
  branchId: number
  myPermssionsVms: MyPermissionVm[]
}

/** GET /api/Permissions/GetPermissions — saved rows for role + module (id = lineId for Save payload) */
export interface PermissionRecord {
  id: number
  roleId: number
  moduleId: number
  menuId: number
  canRead: number
  canUpdate: number
  canDelete: number
  canInsert: number
  branchId?: number
}

function normalizePermissionRecord(raw: Record<string, unknown>): PermissionRecord {
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    roleId: Number(raw.roleId ?? raw.RoleId ?? 0),
    moduleId: Number(raw.moduleId ?? raw.ModuleId ?? 0),
    menuId: Number(raw.menuId ?? raw.MenuId ?? 0),
    canRead: Number(raw.canRead ?? raw.CanRead ?? 0),
    canUpdate: Number(raw.canUpdate ?? raw.CanUpdate ?? 0),
    canDelete: Number(raw.canDelete ?? raw.CanDelete ?? 0),
    canInsert: Number(raw.canInsert ?? raw.CanInsert ?? 0),
    branchId: raw.branchId != null || raw.BranchId != null
      ? Number(raw.branchId ?? raw.BranchId)
      : undefined,
  }
}

function unwrapArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    const inner = p.data ?? p.Data ?? p.items
    if (Array.isArray(inner)) return inner
  }
  return []
}

function normalizeLookupItem(raw: Record<string, unknown>): PermissionLookupItem {
  const lineIdRaw = raw.lineId ?? raw.LineId ?? raw.permissionLineId ?? raw.PermissionLineId
  const lineId = lineIdRaw != null ? Number(lineIdRaw) : undefined

  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    lookupCodeId: Number(raw.lookupCodeId ?? raw.LookupCodeId ?? 0),
    lookupNameEn: String(raw.lookupNameEn ?? raw.LookupNameEn ?? ''),
    lookupNameAr: raw.lookupNameAr != null || raw.LookupNameAr != null
      ? String(raw.lookupNameAr ?? raw.LookupNameAr)
      : undefined,
    isActive: Number(raw.isActive ?? raw.IsActive ?? 1),
    parentCode: Number(raw.parentCode ?? raw.ParentCode ?? 0),
    lineId: lineId != null && !Number.isNaN(lineId) ? lineId : undefined,
    canRead: raw.canRead != null || raw.CanRead != null ? Boolean(raw.canRead ?? raw.CanRead) : undefined,
    canUpdate: raw.canUpdate != null || raw.CanUpdate != null ? Boolean(raw.canUpdate ?? raw.CanUpdate) : undefined,
    canDelete: raw.canDelete != null || raw.CanDelete != null ? Boolean(raw.canDelete ?? raw.CanDelete) : undefined,
    canInsert: raw.canInsert != null || raw.CanInsert != null ? Boolean(raw.canInsert ?? raw.CanInsert) : undefined,
  }
}

/** Response from GET GetPermissionForLoggedInUser — show HR sidebar when moduleId is present and > 0 */
function parseModuleIdFromLoggedInUserPayload(payload: unknown): number | null {
  if (payload == null) return null
  if (typeof payload === 'number' && payload > 0) return payload
  if (typeof payload === 'string') {
    const n = Number(payload)
    return !Number.isNaN(n) && n > 0 ? n : null
  }
  if (typeof payload !== 'object') return null
  const o = payload as Record<string, unknown>
  const direct =
    o.moduleId ?? o.ModuleId ?? o.moduleID ?? o.ModuleID ?? o.id ?? o.Id
  if (typeof direct === 'number' && direct > 0) return direct
  if (typeof direct === 'string') {
    const n = Number(direct)
    if (!Number.isNaN(n) && n > 0) return n
  }
  const nested = o.data ?? o.Data ?? o.result ?? o.Result
  if (nested != null && nested !== o) return parseModuleIdFromLoggedInUserPayload(nested)
  return null
}

class PermissionsService {
  /**
   * Whether the logged-in user has access to the given module (and optional menu) for the branch.
   * For sidebar module visibility, pass empty menuName.
   * @param roleId — same as selected branch’s role (e.g. `branchUserRoleId` from auth store).
   */
  async getPermissionForLoggedInUser(
    moduleName: string,
    menuName: string,
    branchId: number,
    roleId: number
  ): Promise<{ moduleId: number | null }> {
    const url = endpoints.permissions.getPermissionForLoggedInUser(moduleName, menuName, branchId, roleId)
    const response = await apiClient.get<unknown>(url)
    const moduleId = parseModuleIdFromLoggedInUserPayload(response.data)
    return { moduleId }
  }

  async getModules(): Promise<PermissionLookupItem[]> {
    const url = endpoints.permissions.getModules
    const response = await apiClient.get<unknown>(url)
    const list = unwrapArray(response.data)
    return list
      .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
      .map(normalizeLookupItem)
      .filter((m) => m.isActive !== 0)
  }

  /** Pass roleId when API supports returning existing permission lineIds for that role. */
  async getPermissions(roleId: number, moduleId: number, branchId: number): Promise<PermissionRecord[]> {
    const url = endpoints.permissions.getPermissions(roleId, moduleId, branchId)
    const response = await apiClient.get<unknown>(url)
    const list = unwrapArray(response.data)
    return list
      .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
      .map(normalizePermissionRecord)
      .filter((r) => r.id > 0 && r.menuId > 0)
  }

  async getMenuByModuleName(moduleName: string, roleId?: number): Promise<PermissionLookupItem[]> {
    const trimmed = moduleName.trim()
    if (!trimmed) return []

    const url = endpoints.permissions.getMenu(trimmed, roleId)
    const response = await apiClient.get<unknown>(url)
    const list = unwrapArray(response.data)
    return list
      .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
      .map(normalizeLookupItem)
  }

  async savePermissions(payload: SavePermissionsPayload): Promise<unknown> {
    const url = endpoints.permissions.save
    console.log('[Permissions Save] POST', url)
    console.log('[Permissions Save] Body JSON:\n', JSON.stringify(payload, null, 2))
    // Swagger sends application/json-patch+json; some ASP.NET actions only bind with this Consumes type
    const response = await apiClient.post(url, payload, {
      headers: {
        'Content-Type': 'application/json-patch+json',
        Accept: 'application/json',
      },
    })
    return response.data
  }
}

export const permissionsService = new PermissionsService()
